export type ListSortOrder = "asc" | "desc";

export type ListSortCriteria = {
  sortBy?: string;
  sortOrder?: ListSortOrder;
  currentPage?: number;
};

export const createListSort = <TField extends string>(
  fields: readonly TField[],
  columnToField: Record<string, TField>,
  extraFields: Partial<Record<TField, string>> = {}
) => {
  const fieldToColumn: Record<string, string> = {};
  (Object.keys(extraFields) as TField[]).forEach((field) => {
    const columnId = extraFields[field];
    if (columnId) {
      fieldToColumn[field] = columnId;
    }
  });
  Object.entries(columnToField).forEach(([columnId, field]) => {
    fieldToColumn[field] = columnId;
  });

  const allowed = new Set<string>(fields);

  const isSortField = (value: string): value is TField => allowed.has(value);

  const normalize = <T extends ListSortCriteria>(criteria: T): T => {
    const next: T = { ...criteria };
    const sortBy = next.sortBy;
    const sortOrder = next.sortOrder;
    if (!sortBy || !isSortField(sortBy) || (sortOrder !== "asc" && sortOrder !== "desc")) {
      delete next.sortBy;
      delete next.sortOrder;
    }
    return next;
  };

  const withSort = <T extends ListSortCriteria>(
    criteria: T,
    columnId: string,
    order: ListSortOrder
  ): (T & { sortBy: TField; sortOrder: ListSortOrder }) | null => {
    const sortBy = columnToField[columnId];
    if (!sortBy || !isSortField(sortBy)) {
      return null;
    }
    return {
      ...criteria,
      currentPage: 1,
      sortBy,
      sortOrder: order,
    };
  };

  const pick = (criteria?: ListSortCriteria | null): {
    sortBy?: TField;
    sortOrder?: ListSortOrder;
  } => {
    if (!criteria?.sortBy || !criteria.sortOrder || !isSortField(criteria.sortBy)) {
      return {};
    }
    if (criteria.sortOrder !== "asc" && criteria.sortOrder !== "desc") {
      return {};
    }
    return { sortBy: criteria.sortBy, sortOrder: criteria.sortOrder };
  };

  const controlledColumn = (sortBy?: string | null): string | null => {
    if (!sortBy) {
      return null;
    }
    return fieldToColumn[sortBy] ?? null;
  };

  return {
    sortableColumns: Object.keys(columnToField),
    normalize,
    withSort,
    pick,
    controlledColumn,
  };
};
